import React, { useState, useEffect } from 'react';
import { AlertTriangle, AlertCircle, AlertOctagon, Bell, ChevronDown, ChevronRight, Clock, Filter, Plus, Search, Tag, Trash2, User, X, CheckCircle, Ticket, RefreshCw } from 'lucide-react';

// Define ticket severity types
type TicketSeverity = 'critical' | 'high' | 'medium' | 'low';

// Define ticket interface
interface Ticket {
  id: string;
  title: string;
  subject: string;
  severity: TicketSeverity;
  status: 'open' | 'in-progress' | 'resolved';
  createdAt: Date;
  updatedAt: Date;
  assignedTo?: string;
  createdBy: string;
  description?: string;
  analyst?: string; // Keep this field for internal use
}

const TicketStatus = () => {
  // State for tickets
  const [tickets, setTickets] = useState<Ticket[]>(() => {
    // Load from localStorage if available
    const savedTickets = localStorage.getItem('alertstrike_tickets');
    if (savedTickets) {
      try {
        // Parse the JSON string and convert date strings back to Date objects
        return JSON.parse(savedTickets, (key, value) => {
          if (key === 'createdAt' || key === 'updatedAt') {
            return new Date(value);
          }
          return value;
        });
      } catch (e) {
        console.error('Failed to parse saved tickets:', e);
        return [];
      }
    }
    return [];
  });
  
  // Filter and search state
  const [filteredTickets, setFilteredTickets] = useState<Ticket[]>(tickets);
  const [selectedSeverity, setSelectedSeverity] = useState<TicketSeverity | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<'all' | 'open' | 'in-progress' | 'resolved'>('all');
  
  // Form state
  const [showNewTicketForm, setShowNewTicketForm] = useState(false);
  const [newTicket, setNewTicket] = useState<{
    title: string;
    subject: string;
    severity: TicketSeverity;
    description: string;
    analyst: string; // Add analyst field
  }>({
    title: '',
    subject: '',
    severity: 'medium',
    description: '',
    analyst: '' // Initialize analyst field
  });
  
  // Selected ticket for details view
  const [selectedTicket, setSelectedTicket] = useState<string | null>(null);
  
  // Save tickets to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('alertstrike_tickets', JSON.stringify(tickets));
  }, [tickets]);
  
  // Filter tickets whenever filter criteria change
  useEffect(() => {
    let filtered = [...tickets];
    
    // Filter by severity
    if (selectedSeverity !== 'all') {
      filtered = filtered.filter(ticket => ticket.severity === selectedSeverity);
    }
    
    // Filter by status
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(ticket => ticket.status === selectedStatus);
    }
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        ticket => 
          ticket.title.toLowerCase().includes(query) ||
          ticket.subject.toLowerCase().includes(query) ||
          (ticket.description && ticket.description.toLowerCase().includes(query))
      );
    }
    
    // Sort tickets by severity and then by creation date (newest first)
    filtered.sort((a, b) => {
      const severityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
      if (severityOrder[a.severity] !== severityOrder[b.severity]) {
        return severityOrder[a.severity] - severityOrder[b.severity];
      }
      return b.createdAt.getTime() - a.createdAt.getTime();
    });
    
    setFilteredTickets(filtered);
  }, [tickets, selectedSeverity, searchQuery, selectedStatus]);
  
  // Generate ticket counts
  const ticketCounts = {
    all: tickets.length,
    critical: tickets.filter(t => t.severity === 'critical').length,
    high: tickets.filter(t => t.severity === 'high').length,
    medium: tickets.filter(t => t.severity === 'medium').length,
    low: tickets.filter(t => t.severity === 'low').length,
    open: tickets.filter(t => t.status === 'open').length,
    inProgress: tickets.filter(t => t.status === 'in-progress').length,
    resolved: tickets.filter(t => t.status === 'resolved').length
  };
  
  // Handle creating a new ticket
  const handleCreateTicket = () => {
    if (!newTicket.title || !newTicket.subject) {
      alert('Please provide both a title and subject for the ticket.');
      return;
    }
    
    // Check if we've hit the maximum number of tickets (30)
    if (tickets.length >= 30) {
      alert('Maximum number of tickets (30) reached. Please resolve or delete existing tickets before creating new ones.');
      return;
    }
    
    const ticket: Ticket = {
      id: `ticket-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      title: newTicket.title,
      subject: newTicket.subject,
      severity: newTicket.severity,
      status: 'open',
      createdAt: new Date(),
      updatedAt: new Date(),
      // Use analyst name if provided, otherwise use default
      createdBy: newTicket.analyst 
        ? `Created by ${newTicket.analyst}` 
        : 'Security Operations Center',
      description: newTicket.description,
      analyst: newTicket.analyst // Keep the analyst field for potential future use
    };
    
    setTickets(prev => [ticket, ...prev]);
    
    // Reset form
    setNewTicket({
      title: '',
      subject: '',
      severity: 'medium',
      description: '',
      analyst: '' // Reset analyst field
    });
    
    setShowNewTicketForm(false);
  };
  
  // Delete a ticket
  const handleDeleteTicket = (id: string) => {
    if (window.confirm('Are you sure you want to delete this ticket?')) {
      setTickets(prev => prev.filter(t => t.id !== id));
      if (selectedTicket === id) {
        setSelectedTicket(null);
      }
    }
  };
  
  // Update ticket status
  const handleUpdateTicketStatus = (id: string, newStatus: 'open' | 'in-progress' | 'resolved') => {
    setTickets(prev => 
      prev.map(ticket => 
        ticket.id === id 
          ? { ...ticket, status: newStatus, updatedAt: new Date() } 
          : ticket
      )
    );
  };
  
  // Get icon based on severity
  const getSeverityIcon = (severity: TicketSeverity) => {
    switch (severity) {
      case 'critical':
        return <AlertOctagon className="h-4 w-4 text-red-500" />;
      case 'high':
        return <AlertTriangle className="h-4 w-4 text-orange-500" />;
      case 'medium':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      case 'low':
        return <Bell className="h-4 w-4 text-blue-500" />;
    }
  };
  
  // Get color theme based on severity
  const getSeverityTheme = (severity: TicketSeverity) => {
    switch (severity) {
      case 'critical':
        return {
          bg: 'bg-red-900/20',
          border: 'border-red-900/30',
          text: 'text-red-400'
        };
      case 'high':
        return {
          bg: 'bg-orange-900/20',
          border: 'border-orange-900/30',
          text: 'text-orange-400'
        };
      case 'medium':
        return {
          bg: 'bg-yellow-900/20',
          border: 'border-yellow-900/30',
          text: 'text-yellow-400'
        };
      case 'low':
        return {
          bg: 'bg-blue-900/20',
          border: 'border-blue-900/30',
          text: 'text-blue-400'
        };
    }
  };

  // Format date to readable string
  const formatDate = (date: Date) => {
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Get status with appropriate styling
  const getStatusElement = (status: 'open' | 'in-progress' | 'resolved') => {
    switch (status) {
      case 'open':
        return (
          <span className="bg-yellow-900/20 text-yellow-400 text-xs px-2 py-0.5 rounded-full border border-yellow-900/30 flex items-center">
            <AlertCircle className="h-3 w-3 mr-1" />
            Open
          </span>
        );
      case 'in-progress':
        return (
          <span className="bg-blue-900/20 text-blue-400 text-xs px-2 py-0.5 rounded-full border border-blue-900/30 flex items-center">
            <RefreshCw className="h-3 w-3 mr-1" />
            In Progress
          </span>
        );
      case 'resolved':
        return (
          <span className="bg-green-900/20 text-green-400 text-xs px-2 py-0.5 rounded-full border border-green-900/30 flex items-center">
            <CheckCircle className="h-3 w-3 mr-1" />
            Resolved
          </span>
        );
    }
  };
  
  return (
    <div className="container mx-auto px-3 sm:px-6 py-4 sm:py-8">
      {/* HUD-Style Top Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-10 p-1 border border-red-900/30 rounded-md bg-black/30 backdrop-blur-sm">
        <div className="flex flex-wrap items-center gap-2 px-2 py-2 sm:py-0 sm:gap-4 sm:px-4">
          <div className="text-red-500 font-mono text-xs sm:text-sm">
            <span className="text-slate-400">TICKETS:</span> {ticketCounts.all}
          </div>
          <div className="text-red-500 font-mono text-xs sm:text-sm">
            <span className="text-slate-400">OPEN:</span> {ticketCounts.open}
          </div>
          <div className="text-red-500 font-mono text-xs sm:text-sm">
            <span className="text-slate-400">CRITICAL:</span> {ticketCounts.critical}
          </div>
        </div>
        <div className="flex items-center px-2 py-2 sm:py-0">
          <div className="h-2 bg-gradient-to-r from-red-900 via-red-600 to-red-900 animate-pulse mx-2 sm:mx-4" style={{ width: '60px', maxWidth: '25vw' }}></div>
          <div className="text-red-400 font-mono text-xs px-2 sm:px-4">
            <span className="text-slate-500">TIME:</span> {new Date().toLocaleTimeString()}
          </div>
        </div>
      </div>

      {/* Page Header */}
      <div className="mb-8">
        <div className="relative">
          <h1 className="text-2xl sm:text-4xl font-bold mb-1">
            Ticket <span className="text-red-500">Status</span>
          </h1>
          <div className="absolute -top-2 -right-2 w-2 h-2 bg-red-600 rounded-full animate-ping"></div>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-red-400 text-xs sm:text-base mb-4">
          <Ticket className="h-4 w-4 sm:h-5 sm:w-5" />
          <span>Security Incident Management</span>
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 hidden sm:block"></span>
          <span className="hidden sm:inline">AlertStrike™ Ticketing System</span>
        </div>
        <p className="text-slate-300 text-xs sm:text-sm border-l-4 border-red-900/50 pl-3 pr-4 py-2 bg-black/30 rounded-r-md mb-6">
          Create and manage security incident tickets with different severity levels. Track the status of 
          ongoing security issues, assign them to team members, and maintain a record of resolved incidents.
          The system can manage up to 30 active tickets simultaneously.
        </p>
      </div>

      {/* Tickets Overview */}
      <div className="mb-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Critical Tickets */}
          <div className="bg-black/40 border border-slate-800 rounded-lg p-4 hover:border-red-900/30 transition-colors">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center">
                <div className="p-2 bg-red-900/20 rounded-lg border border-red-900/30 mr-3">
                  <AlertOctagon className="h-5 w-5 text-red-500" />
                </div>
                <div>
                  <h3 className="font-bold text-white">Critical</h3>
                  <p className="text-xs text-slate-400">Highest Priority</p>
                </div>
              </div>
              <div className="text-2xl font-bold text-red-500">{ticketCounts.critical}</div>
            </div>
            <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
              <div 
                className="h-full bg-red-600 rounded-full" 
                style={{ width: `${(ticketCounts.critical / Math.max(1, ticketCounts.all)) * 100}%` }}
              ></div>
            </div>
            <div className="flex items-center justify-between mt-3 text-xs text-slate-400">
              <span>Requires immediate action</span>
              <button 
                onClick={() => {
                  setSelectedSeverity('critical');
                  setSelectedStatus('all');
                }}
                className="text-red-400 hover:text-red-300 transition-colors"
              >
                View All
              </button>
            </div>
          </div>

          {/* High Tickets */}
          <div className="bg-black/40 border border-slate-800 rounded-lg p-4 hover:border-red-900/30 transition-colors">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center">
                <div className="p-2 bg-orange-900/20 rounded-lg border border-orange-900/30 mr-3">
                  <AlertTriangle className="h-5 w-5 text-orange-500" />
                </div>
                <div>
                  <h3 className="font-bold text-white">High</h3>
                  <p className="text-xs text-slate-400">Urgent Issues</p>
                </div>
              </div>
              <div className="text-2xl font-bold text-orange-500">{ticketCounts.high}</div>
            </div>
            <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
              <div 
                className="h-full bg-orange-600 rounded-full" 
                style={{ width: `${(ticketCounts.high / Math.max(1, ticketCounts.all)) * 100}%` }}
              ></div>
            </div>
            <div className="flex items-center justify-between mt-3 text-xs text-slate-400">
              <span>Significant business impact</span>
              <button 
                onClick={() => {
                  setSelectedSeverity('high');
                  setSelectedStatus('all');
                }}
                className="text-orange-400 hover:text-orange-300 transition-colors"
              >
                View All
              </button>
            </div>
          </div>

          {/* Medium Tickets */}
          <div className="bg-black/40 border border-slate-800 rounded-lg p-4 hover:border-red-900/30 transition-colors">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center">
                <div className="p-2 bg-yellow-900/20 rounded-lg border border-yellow-900/30 mr-3">
                  <AlertCircle className="h-5 w-5 text-yellow-500" />
                </div>
                <div>
                  <h3 className="font-bold text-white">Medium</h3>
                  <p className="text-xs text-slate-400">Normal Priority</p>
                </div>
              </div>
              <div className="text-2xl font-bold text-yellow-500">{ticketCounts.medium}</div>
            </div>
            <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
              <div 
                className="h-full bg-yellow-600 rounded-full" 
                style={{ width: `${(ticketCounts.medium / Math.max(1, ticketCounts.all)) * 100}%` }}
              ></div>
            </div>
            <div className="flex items-center justify-between mt-3 text-xs text-slate-400">
              <span>Standard response time</span>
              <button 
                onClick={() => {
                  setSelectedSeverity('medium');
                  setSelectedStatus('all');
                }}
                className="text-yellow-400 hover:text-yellow-300 transition-colors"
              >
                View All
              </button>
            </div>
          </div>

          {/* Low Tickets */}
          <div className="bg-black/40 border border-slate-800 rounded-lg p-4 hover:border-red-900/30 transition-colors">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center">
                <div className="p-2 bg-blue-900/20 rounded-lg border border-blue-900/30 mr-3">
                  <Bell className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <h3 className="font-bold text-white">Low</h3>
                  <p className="text-xs text-slate-400">Minor Issues</p>
                </div>
              </div>
              <div className="text-2xl font-bold text-blue-500">{ticketCounts.low}</div>
            </div>
            <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
              <div 
                className="h-full bg-blue-600 rounded-full" 
                style={{ width: `${(ticketCounts.low / Math.max(1, ticketCounts.all)) * 100}%` }}
              ></div>
            </div>
            <div className="flex items-center justify-between mt-3 text-xs text-slate-400">
              <span>Minimal impact</span>
              <button 
                onClick={() => {
                  setSelectedSeverity('low');
                  setSelectedStatus('all');
                }}
                className="text-blue-400 hover:text-blue-300 transition-colors"
              >
                View All
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Action Bar */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <button 
          onClick={() => setShowNewTicketForm(true)}
          className="flex items-center justify-center bg-red-900/30 border border-red-900/40 text-white rounded-lg px-4 py-2 hover:bg-red-900/50 transition-colors"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create New Ticket
        </button>
        
        <div className="relative flex-grow max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-4 w-4 text-slate-500" />
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search tickets..."
            className="block w-full pl-10 pr-4 py-2 bg-black/50 border border-slate-800 rounded-lg focus:outline-none focus:border-red-900/50 text-white placeholder-slate-500 text-sm"
          />
          {searchQuery && (
            <button
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              onClick={() => setSearchQuery('')}
            >
              <X className="h-4 w-4 text-slate-500 hover:text-white" />
            </button>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          <div className="bg-black/50 border border-slate-800 rounded-lg px-3 py-2 text-sm flex items-center">
            <Filter className="h-4 w-4 text-slate-500 mr-2" />
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value as any)}
              className="bg-transparent text-white focus:outline-none"
            >
              <option value="all" className="bg-slate-950">All Status</option>
              <option value="open" className="bg-slate-950">Open</option>
              <option value="in-progress" className="bg-slate-950">In Progress</option>
              <option value="resolved" className="bg-slate-950">Resolved</option>
            </select>
          </div>
          
          <div className="bg-black/50 border border-slate-800 rounded-lg px-3 py-2 text-sm flex items-center">
            <Tag className="h-4 w-4 text-slate-500 mr-2" />
            <select
              value={selectedSeverity}
              onChange={(e) => setSelectedSeverity(e.target.value as any)}
              className="bg-transparent text-white focus:outline-none"
            >
              <option value="all" className="bg-slate-950">All Severity</option>
              <option value="critical" className="bg-slate-950">Critical</option>
              <option value="high" className="bg-slate-950">High</option>
              <option value="medium" className="bg-slate-950">Medium</option>
              <option value="low" className="bg-slate-950">Low</option>
            </select>
          </div>
        </div>
      </div>

      {/* Ticket List and Details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Ticket List */}
        <div className={`${selectedTicket ? 'hidden lg:block' : ''} lg:col-span-${selectedTicket ? '1' : '3'}`}>
          {filteredTickets.length === 0 ? (
            <div className="bg-black/30 border border-slate-800 rounded-lg p-6 text-center">
              <div className="bg-slate-900/70 rounded-full h-16 w-16 flex items-center justify-center mx-auto mb-4">
                <Ticket className="h-8 w-8 text-slate-600" />
              </div>
              <h3 className="text-lg font-bold mb-2">No Tickets Found</h3>
              <p className="text-slate-400 text-sm mb-4">
                {tickets.length === 0 
                  ? "You haven't created any tickets yet." 
                  : "No tickets match your current filters."}
              </p>
              {tickets.length === 0 ? (
                <button 
                  onClick={() => setShowNewTicketForm(true)}
                  className="inline-flex items-center px-4 py-2 bg-red-900/30 border border-red-900/40 rounded-lg text-white hover:bg-red-900/50 transition-colors"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First Ticket
                </button>
              ) : (
                <button 
                  onClick={() => {
                    setSelectedSeverity('all');
                    setSelectedStatus('all');
                    setSearchQuery('');
                  }}
                  className="inline-flex items-center px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white hover:bg-slate-700 transition-colors"
                >
                  Clear All Filters
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredTickets.map(ticket => (
                <div 
                  key={ticket.id}
                  className={`bg-black/40 border ${selectedTicket === ticket.id ? 'border-red-900/50' : 'border-slate-800'} rounded-lg p-4 hover:border-red-900/30 cursor-pointer transition-colors`}
                  onClick={() => setSelectedTicket(ticket.id)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-start">
                      <div className={`p-2 ${getSeverityTheme(ticket.severity).bg} rounded-lg ${getSeverityTheme(ticket.severity).border} mr-3 mt-1`}>
                        {getSeverityIcon(ticket.severity)}
                      </div>
                      <div>
                        <h3 className="font-bold text-white">{ticket.title}</h3>
                        <p className="text-sm text-slate-300 mb-1">{ticket.subject}</p>
                        <div className="flex flex-wrap items-center gap-2 text-xs">
                          {getStatusElement(ticket.status)}
                          <span className="text-slate-400 flex items-center">
                            <Clock className="h-3 w-3 mr-1" />
                            {formatDate(ticket.createdAt)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Ticket Details */}
        {selectedTicket && (
          <div className="bg-black/40 border border-slate-800 rounded-lg p-4 sm:p-6 lg:col-span-2">
            {tickets.find(t => t.id === selectedTicket) && (() => {
              const ticket = tickets.find(t => t.id === selectedTicket)!;
              const theme = getSeverityTheme(ticket.severity);
              
              return (
                <div>
                  <div className="flex justify-between items-start mb-6">
                    <div className="flex items-start">
                      <div className={`p-3 ${theme.bg} rounded-lg ${theme.border} mr-4`}>
                        {getSeverityIcon(ticket.severity)}
                      </div>
                      <div>
                        <h2 className="text-xl font-bold">{ticket.title}</h2>
                        <p className="text-slate-300">{ticket.subject}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button 
                        onClick={() => setSelectedTicket(null)}
                        className="p-2 bg-black/60 rounded-lg border border-slate-800 hover:border-red-900/30 text-slate-400 hover:text-white transition-colors md:hidden"
                      >
                        <X className="h-4 w-4" />
                      </button>
                      <button 
                        onClick={() => handleDeleteTicket(ticket.id)}
                        className="p-2 bg-black/60 rounded-lg border border-slate-800 hover:border-red-900/30 text-slate-400 hover:text-red-400 transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-black/60 rounded-lg border border-slate-800 p-3">
                      <p className="text-xs text-slate-500 mb-1">Status</p>
                      <div className="text-sm">{getStatusElement(ticket.status)}</div>
                    </div>
                    
                    <div className="bg-black/60 rounded-lg border border-slate-800 p-3">
                      <p className="text-xs text-slate-500 mb-1">Severity</p>
                      <div className={`text-sm ${theme.text} flex items-center`}>
                        {getSeverityIcon(ticket.severity)}
                        <span className="ml-1 capitalize">{ticket.severity}</span>
                      </div>
                    </div>
                    
                    <div className="bg-black/60 rounded-lg border border-slate-800 p-3">
                      <p className="text-xs text-slate-500 mb-1">Created By</p>
                      <div className="text-sm flex items-center">
                        <User className="h-3 w-3 text-slate-400 mr-2" />
                        <span>{ticket.createdBy}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <h3 className="text-sm font-medium mb-2">Description</h3>
                    <div className="bg-black/60 rounded-lg border border-slate-800 p-4 text-sm text-slate-300">
                      {ticket.description || "No description provided."}
                    </div>
                  </div>
                  
                  <div className="border-t border-slate-800 pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-medium">Ticket Timeline</h3>
                      <div className="text-xs text-slate-400 flex items-center">
                        <Clock className="h-3 w-3 mr-1" />
                        Last Updated: {formatDate(ticket.updatedAt)}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <button
                        onClick={() => handleUpdateTicketStatus(ticket.id, 'open')}
                        className={`text-sm py-2 rounded-lg border flex items-center justify-center transition-colors ${
                          ticket.status === 'open'
                            ? 'bg-yellow-900/20 border-yellow-900/40 text-yellow-300'
                            : 'bg-black/40 border-slate-800 text-slate-400 hover:bg-yellow-900/10 hover:border-yellow-900/30'
                        }`}
                      >
                        <AlertCircle className="h-4 w-4 mr-2" />
                        Open
                      </button>
                      
                      <button
                        onClick={() => handleUpdateTicketStatus(ticket.id, 'in-progress')}
                        className={`text-sm py-2 rounded-lg border flex items-center justify-center transition-colors ${
                          ticket.status === 'in-progress'
                            ? 'bg-blue-900/20 border-blue-900/40 text-blue-300'
                            : 'bg-black/40 border-slate-800 text-slate-400 hover:bg-blue-900/10 hover:border-blue-900/30'
                        }`}
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        In Progress
                      </button>
                      
                      <button
                        onClick={() => handleUpdateTicketStatus(ticket.id, 'resolved')}
                        className={`text-sm py-2 rounded-lg border flex items-center justify-center transition-colors ${
                          ticket.status === 'resolved'
                            ? 'bg-green-900/20 border-green-900/40 text-green-300'
                            : 'bg-black/40 border-slate-800 text-slate-400 hover:bg-green-900/10 hover:border-green-900/30'
                        }`}
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Resolved
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* New Ticket Form Modal */}
      {showNewTicketForm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 px-4">
          <div className="bg-slate-950 border border-slate-800 rounded-lg w-full max-w-md relative">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-900 via-red-600 to-red-900"></div>
            
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold flex items-center">
                  <Plus className="h-5 w-5 mr-2 text-red-500" />
                  Create New Ticket
                </h2>
                <button
                  onClick={() => setShowNewTicketForm(false)}
                  className="p-2 bg-black/60 rounded-lg border border-slate-800 hover:border-red-900/30 text-slate-400 hover:text-white transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-slate-400 mb-1">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="title"
                    value={newTicket.title}
                    onChange={(e) => setNewTicket({ ...newTicket, title: e.target.value })}
                    className="block w-full px-3 py-2 bg-black/60 border border-slate-800 rounded-md focus:outline-none focus:border-red-900/50 text-white placeholder-slate-500 text-sm"
                    placeholder="Brief title for the ticket"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-slate-400 mb-1">
                    Subject <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="subject"
                    value={newTicket.subject}
                    onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                    className="block w-full px-3 py-2 bg-black/60 border border-slate-800 rounded-md focus:outline-none focus:border-red-900/50 text-white placeholder-slate-500 text-sm"
                    placeholder="Subject of the security incident"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="analyst" className="block text-sm font-medium text-slate-400 mb-1">
                    Security Analyst Name
                  </label>
                  <input
                    type="text"
                    id="analyst"
                    value={newTicket.analyst}
                    onChange={(e) => setNewTicket({ ...newTicket, analyst: e.target.value })}
                    className="block w-full px-3 py-2 bg-black/60 border border-slate-800 rounded-md focus:outline-none focus:border-red-900/50 text-white placeholder-slate-500 text-sm"
                    placeholder="Enter your name (optional)"
                  />
                </div>
                
                <div>
                  <label htmlFor="severity" className="block text-sm font-medium text-slate-400 mb-1">
                    Severity Level
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {(['critical', 'high', 'medium', 'low'] as const).map((severity) => (
                      <button
                        key={severity}
                        type="button"
                        onClick={() => setNewTicket({ ...newTicket, severity })}
                        className={`py-2 px-3 rounded-md border text-xs flex flex-col items-center ${
                          newTicket.severity === severity
                            ? `${getSeverityTheme(severity).bg} ${getSeverityTheme(severity).border} ${getSeverityTheme(severity).text}`
                            : 'bg-black/40 border-slate-800 text-slate-400 hover:bg-slate-900'
                        }`}
                      >
                        {getSeverityIcon(severity)}
                        <span className="mt-1 capitalize">{severity}</span>
                      </button>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-slate-400 mb-1">
                    Description
                  </label>
                  <textarea
                    id="description"
                    value={newTicket.description}
                    onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                    rows={4}
                    className="block w-full px-3 py-2 bg-black/60 border border-slate-800 rounded-md focus:outline-none focus:border-red-900/50 text-white placeholder-slate-500 text-sm"
                    placeholder="Detailed description of the security incident or issue"
                  />
                </div>
                
                <div className="flex justify-end space-x-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowNewTicketForm(false)}
                    className="px-4 py-2 bg-black/60 border border-slate-800 rounded-md text-slate-300 hover:bg-slate-900 transition-colors text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleCreateTicket}
                    className="px-4 py-2 bg-red-900/30 border border-red-900/40 rounded-md text-white hover:bg-red-900/50 transition-colors text-sm"
                  >
                    Create Ticket
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketStatus;